import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Coins, CreditCard, TrendingUp, TrendingDown, Star, Check, X,
  Building2, Zap, ArrowRight, Brain, BarChart3, CalendarDays, Shuffle, Clock
} from 'lucide-react';

export default function BillingPage() {
  const { i18n } = useTranslation();
  const { api, user, refreshUser } = useAuth();
  const [plans, setPlans] = useState({});
  const [ledger, setLedger] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const lang = i18n.language?.startsWith('tr') ? 'tr' : 'en';

  useEffect(() => {
    Promise.all([
      api.get('/api/plans'),
      api.get('/api/credits/ledger'),
      api.get('/api/plans/current'),
    ]).then(([plansRes, ledgerRes, currentRes]) => {
      setPlans(plansRes.data);
      setLedger(ledgerRes.data.ledger || []);
      setCurrentPlan(currentRes.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  const handleUpgrade = async (planKey) => {
    try {
      const res = await api.put(`/api/plans/upgrade?plan=${planKey}`);
      toast.success(res.data.message);
      refreshUser();
      const currentRes = await api.get('/api/plans/current');
      setCurrentPlan(currentRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upgrade failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--primary))]"></div>
      </div>
    );
  }

  const planOrder = ['starter', 'pro', 'agency'];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        {lang === 'tr' ? 'Faturalandırma & Planlar' : 'Billing & Plans'}
      </h1>

      {/* Balance + Current Plan */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-sheen rounded-xl border border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))]/5">
          <CardContent className="p-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{lang === 'tr' ? 'Mevcut Bakiye' : 'Current Balance'}</p>
            <div className="flex items-center gap-2">
              <Coins size={28} className="text-[hsl(var(--primary))]" />
              <span className="text-4xl font-bold tabular-nums" data-testid="billing-balance">{user?.credits || 0}</span>
              <span className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'kredi' : 'credits'}</span>
            </div>
          </CardContent>
        </Card>
        <Card className="card-sheen rounded-xl border border-[hsl(var(--accent))]/20 bg-[hsl(var(--accent))]/5">
          <CardContent className="p-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{lang === 'tr' ? 'Mevcut Plan' : 'Current Plan'}</p>
            <div className="flex items-center gap-2">
              <Zap size={28} className="text-[hsl(var(--accent))]" />
              <span className="text-2xl font-bold">{currentPlan?.name || 'Starter'}</span>
              {currentPlan?.plan === 'agency' && (
                <Badge className="bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))] border-0">
                  <Building2 size={10} className="mr-1" /> Agency
                </Badge>
              )}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              {lang === 'tr'
                ? `AI maliyet çarpanı: x${currentPlan?.credit_multiplier || 1.5}`
                : `AI cost multiplier: x${currentPlan?.credit_multiplier || 1.5}`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Plans */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {lang === 'tr' ? 'Planlar' : 'Plans'}
          </h2>
          <Tabs defaultValue="monthly" onValueChange={setBillingCycle}>
            <TabsList className="bg-[hsl(var(--secondary))]">
              <TabsTrigger value="monthly">{lang === 'tr' ? 'Aylık' : 'Monthly'}</TabsTrigger>
              <TabsTrigger value="yearly">
                {lang === 'tr' ? 'Yıllık' : 'Yearly'}
                <Badge className="ml-2 bg-emerald-500/20 text-emerald-300 border-0 text-[9px]">-17%</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {planOrder.map(planKey => {
            const plan = plans[planKey];
            if (!plan) return null;
            const price = billingCycle === 'monthly' ? plan.price_monthly : Math.round(plan.price_yearly / 12);
            const isPopular = plan.popular;
            const isCurrent = currentPlan?.plan === planKey;
            const features = plan.display_features?.[lang] || plan.display_features?.en || [];
            const disabled = plan.disabled_features?.[lang] || plan.disabled_features?.en || [];

            return (
              <Card key={planKey} className={`card-sheen rounded-xl border bg-[hsl(var(--card))]/80 ${
                isCurrent ? 'border-[hsl(var(--accent))]/40 ring-1 ring-[hsl(var(--accent))]/20'
                : isPopular ? 'border-[hsl(var(--primary))]/40 ring-1 ring-[hsl(var(--primary))]/20'
                : 'border-white/10'
              }`}>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    {isCurrent && (
                      <Badge className="bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))] border-0 text-[10px]">
                        {lang === 'tr' ? 'Mevcut' : 'Current'}
                      </Badge>
                    )}
                    {isPopular && !isCurrent && (
                      <Badge className="bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30">
                        <Star size={10} className="mr-1" /> {lang === 'tr' ? 'Popüler' : 'Popular'}
                      </Badge>
                    )}
                    {planKey === 'agency' && (
                      <Badge className="bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))] border-0 text-[10px]">
                        <Building2 size={10} className="mr-1" /> Agency
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold tabular-nums">${price}</span>
                    <span className="text-sm text-[hsl(var(--muted-foreground))]">/ {lang === 'tr' ? 'ay' : 'mo'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Coins size={14} className="text-[hsl(var(--primary))]" />
                    <span className="tabular-nums font-medium">{plan.credits_monthly}</span>
                    <span className="text-[hsl(var(--muted-foreground))] text-xs">{lang === 'tr' ? 'kredi/ay' : 'credits/mo'}</span>
                  </div>
                  {plan.credit_multiplier < 1 && (
                    <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px]">
                      AI x{plan.credit_multiplier}
                    </Badge>
                  )}
                  <ul className="space-y-1.5">
                    {features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                        <Check size={12} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                    {disabled.map((f, fi) => (
                      <li key={`d-${fi}`} className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]/40 line-through">
                        <X size={12} className="text-gray-600 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button disabled className="w-full bg-[hsl(var(--secondary))]" data-testid={`plan-${planKey}`}>
                      {lang === 'tr' ? 'Mevcut Plan' : 'Current Plan'}
                    </Button>
                  ) : (
                    <Button onClick={() => handleUpgrade(planKey)}
                      className={`w-full ${isPopular ? 'gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'bg-[hsl(var(--secondary))] border border-white/10'}`}
                      data-testid={`plan-${planKey}`}>
                      {lang === 'tr' ? 'Yükselt' : 'Upgrade'} <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Transaction History */}
      <div>
        <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {lang === 'tr' ? 'İşlem Geçmişi' : 'Transaction History'}
        </h2>
        <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
          <CardContent className="p-0">
            {ledger.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard size={40} className="mx-auto mb-3 text-[hsl(var(--muted-foreground))]" />
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Henüz işlem yok' : 'No transactions yet'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Miktar' : 'Amount'}</TableHead>
                    <TableHead className="text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Tür' : 'Type'}</TableHead>
                    <TableHead className="text-[hsl(var(--muted-foreground))]">{lang === 'tr' ? 'Açıklama' : 'Description'}</TableHead>
                    <TableHead className="text-[hsl(var(--muted-foreground))] text-right">{lang === 'tr' ? 'Tarih' : 'Date'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.map((item, i) => (
                    <TableRow key={i} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <span className={`flex items-center gap-1 font-medium tabular-nums ${item.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {item.amount > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {item.amount > 0 ? '+' : ''}{item.amount}
                        </span>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] border-white/20">{item.type}</Badge></TableCell>
                      <TableCell className="text-sm text-[hsl(var(--muted-foreground))]">{item.description}</TableCell>
                      <TableCell className="text-right text-xs text-[hsl(var(--muted-foreground))]">{new Date(item.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
